import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/app/common/common-functions/functions";
import { forwardRef } from "react";

const headingVariants = cva("", {
  variants: {
    level: {
      1: "tw-text-3xl tw-font-semibold",
      2: "tw-text-2xl tw-font-semibold",
      3: "tw-text-xl tw-font-semibold",
      4: "tw-text-lg tw-font-semibold",
      5: "tw-text-base tw-font-semibold",
      6: "tw-text-sm tw-font-semibold",
    },
    variant: {
      sub: "tw-text-muted-foreground",
    },
  },
  defaultVariants: {
    level: 1,
  },
});

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  asChild?: boolean;
}

const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level, variant, asChild = false, ...props }, ref) => {
    const commonProps = {
      className: cn(headingVariants({ level, variant, className })),
      ref,
      ...props,
    };

    if (asChild) {
      return <Slot {...commonProps} />;
    }

    switch (level) {
      case 1:
        return <h1 {...commonProps} />;
      case 2:
        return <h2 {...commonProps} />;
      case 3:
        return <h3 {...commonProps} />;
      case 4:
        return <h4 {...commonProps} />;
      case 5:
        return <h5 {...commonProps} />;
      case 6:
        return <h6 {...commonProps} />;
      default:
        return <h1 {...commonProps} />;
    }
  }
);

Heading.displayName = "Heading";

export { Heading, headingVariants };
